// Routine run history (HERMES B1): the wire run-post → what the Run history
// list renders. Kept apart from hosted-agent-routines.ts (form state) because
// this is purely about reading the ledger's vocabulary.

import type {
  HostedAgentRoutineRun,
  HostedAgentRoutineRunWarning,
} from "@/api/admin/hosted-agents-routines"

/** The Run history badge set (UI vocabulary, not the ledger's). */
export type RoutineRunBadgeStatus = "queued" | "running" | "succeeded" | "failed" | "cancelled"

/**
 * The run ledger speaks `pending | running | completed | failed | interrupted`
 * (`services/hosted_agents/runs.py`); the badge set is
 * `queued | running | succeeded | failed | cancelled`. Rendering the raw value
 * produced "completed · Pending" for a finished run (#3207).
 */
const LEDGER_TO_BADGE: Record<string, RoutineRunBadgeStatus> = {
  pending: "queued",
  queued: "queued",
  running: "running",
  completed: "succeeded",
  succeeded: "succeeded",
  failed: "failed",
  interrupted: "cancelled",
  cancelled: "cancelled",
}

/** How often the open Run history re-reads while a run is still in flight. */
export const ROUTINE_RUN_HISTORY_POLL_MS = 3000

/**
 * The run-post as the backend serializes it (`_run_summary`): the run's
 * timestamp is `created_at` (queued) / `claimed_at` (picked up), a failure is
 * an `error: {code, message}` object, and non-fatal notes arrive in
 * `warnings`. The legacy `started_at` / `error_code` names are read as
 * fallbacks, and every field is treated as untrusted so a partial post renders.
 */
type RoutineRunWire = Omit<Partial<HostedAgentRoutineRun>, "error" | "warnings"> & {
  error?: { code?: unknown } | string | null
  warnings?: unknown
}

export type RoutineRunView = {
  runId: string
  status: RoutineRunBadgeStatus | "unknown"
  /** Raw ledger value, kept for the badge label fallback when it is unknown. */
  rawStatus: string
  /** When the run was queued (ISO), or null when the post carries no timestamp. */
  startedAt: string | null
  durationMs: number | null
  errorCode: string | null
  /** Non-fatal notes on a run that still executed; [] when the post has none. */
  warnings: HostedAgentRoutineRunWarning[]
}

export function routineRunBadgeStatus(status: unknown): RoutineRunBadgeStatus | "unknown" {
  return typeof status === "string" ? (LEDGER_TO_BADGE[status] ?? "unknown") : "unknown"
}

function stringOrNull(value: unknown): string | null {
  return typeof value === "string" && value ? value : null
}

function runErrorCode(run: RoutineRunWire): string | null {
  const { error } = run
  if (error && typeof error === "object") return stringOrNull(error.code)
  return stringOrNull(run.error_code) ?? stringOrNull(error)
}

/**
 * The `warnings` a run-post or a run-now response carries, e.g.
 * `routine_owner_groups_unresolved`. Older backends send none; entries without
 * a code or message are dropped rather than rendered blank.
 */
export function routineRunWarnings(value: unknown): HostedAgentRoutineRunWarning[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((entry: unknown) => {
    if (!entry || typeof entry !== "object") return []
    const { code, message } = entry as { code?: unknown; message?: unknown }
    const codeText = stringOrNull(code)
    const messageText = typeof message === "string" ? message.trim() : ""
    if (!codeText && !messageText) return []
    return [{ code: codeText ?? "", message: messageText }]
  })
}

export function toRoutineRunView(run: RoutineRunWire): RoutineRunView {
  return {
    runId: stringOrNull(run.run_id) ?? "",
    status: routineRunBadgeStatus(run.status),
    rawStatus: typeof run.status === "string" ? run.status : "",
    startedAt:
      stringOrNull(run.created_at) ?? stringOrNull(run.claimed_at) ?? stringOrNull(run.started_at),
    durationMs: typeof run.duration_ms === "number" ? run.duration_ms : null,
    errorCode: runErrorCode(run),
    warnings: routineRunWarnings(run.warnings),
  }
}

/** A queued or running run will still change: keep the open history polling. */
export function isRoutineRunInFlight(run: Pick<RoutineRunView, "status">): boolean {
  return run.status === "queued" || run.status === "running"
}
